/**
 * EmotiMate 管理後台前端（esbuild bundle → dist/admin-web/admin.js）。
 *
 * 🔒 安全要點：
 *   - 申請人自己填嘅資料（稱呼、地區、渠道、電郵）一律用 textContent 放入 DOM，
 *     唔用 innerHTML —— 否則有人喺申請表填 <img onerror=...> 就喺 admin 瀏覽器執行
 *   - 所有請求帶 X-Admin-Request: 1（server 嘅 CSRF 檢查）
 *   - 電郵登入連結要配合呢個瀏覽器 localStorage 入面嘅 nonce 先用得
 */
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";

const NONCE_KEY = "emotimate_admin_nonce";
const app = document.getElementById("app")!;
const whoami = document.getElementById("whoami")!;
const whoamiEmail = document.getElementById("whoami-email")!;
const logoutBtn = document.getElementById("logout")!;

// ------------------------------------------------------------------
// 小工具
// ------------------------------------------------------------------

type Attrs = Record<string, string | boolean | undefined>;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Array<Node | string | null | undefined>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k === "class") node.className = String(v);
    else node.setAttribute(k, v === true ? "" : String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined) continue;
    node.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method || "GET",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-Admin-Request": "1" },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error || `HTTP ${res.status}`);
  return data as T;
}

function randomNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("zh-HK", { dateStyle: "medium", timeStyle: "short" }) : "—";

function notice(kind: "ok" | "err" | "info", text: string) {
  return el("div", { class: `notice notice-${kind}`, role: kind === "err" ? "alert" : "status" }, text);
}

const ERROR_TEXT: Record<string, string> = {
  link_expired_or_other_browser: "連結已經用過、過咗期，或者唔係喺申請登入嗰個瀏覽器打開。請喺呢個瀏覽器重新申請。",
  link_invalid: "登入連結無效，請重新申請。",
  passkey_registration_failed: "Passkey 登記失敗，請再試一次。",
  passkey_login_failed: "Passkey 驗證失敗，請再試一次。",
  unknown_credential: "呢個 Passkey 唔屬於你嘅 admin 帳戶。",
  invalid_email: "電郵格式唔啱。",
  request_not_pending: "呢個申請已經處理咗。",
  not_admin: "你冇 admin 權限。",
  admin_login_required: "登入已過期，請重新登入。",
};
const errText = (e: unknown) => ERROR_TEXT[(e as Error)?.message] || `出錯：${(e as Error)?.message || e}`;

function setView(...nodes: Node[]) {
  app.replaceChildren(...nodes);
}

// ------------------------------------------------------------------
// 登入
// ------------------------------------------------------------------

function renderLogin(message?: HTMLElement) {
  whoami.hidden = true;
  const email = el("input", { type: "email", id: "login-email", autocomplete: "email", required: true, placeholder: "admin@example.com" });
  const status = el("div");
  const submit = el("button", { type: "submit", class: "btn btn-block" }, "寄登入連結");
  const form = el(
    "form",
    {},
    el("label", { for: "login-email" }, "Admin 電郵"),
    email,
    submit,
    status
  );
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    const nonce = randomNonce();
    localStorage.setItem(NONCE_KEY, nonce);
    try {
      await api("/api/login/request", { method: "POST", body: { email: email.value, nonce } });
      status.replaceChildren(
        notice("ok", "如果呢個係 admin 電郵，登入連結已經寄出。請喺呢個瀏覽器打開信入面嘅連結（15 分鐘內有效）。")
      );
    } catch (err) {
      status.replaceChildren(notice("err", errText(err)));
    } finally {
      submit.disabled = false;
    }
  });

  setView(
    el(
      "section",
      { class: "card narrow" },
      el("h1", {}, "管理後台登入"),
      el("p", { class: "muted small" }, "要用電郵連結加 Passkey（Face ID／指紋）兩樣驗證先入到。"),
      message ?? null,
      form
    )
  );
}

async function handleLinkFromEmail(token: string) {
  history.replaceState(null, "", location.pathname);
  const nonce = localStorage.getItem(NONCE_KEY);
  if (!nonce) {
    renderLogin(notice("err", ERROR_TEXT.link_expired_or_other_browser));
    return;
  }
  setView(el("section", { class: "card narrow" }, el("p", { class: "muted" }, "驗證緊登入連結…")));
  try {
    const r = await api<{ email: string; needsPasskeyRegistration: boolean }>("/api/login/verify", {
      method: "POST",
      body: { token, nonce },
    });
    localStorage.removeItem(NONCE_KEY);
    renderPasskeyStep(r.email, r.needsPasskeyRegistration);
  } catch (err) {
    renderLogin(notice("err", errText(err)));
  }
}

function renderPasskeyStep(email: string, needsRegistration: boolean) {
  const status = el("div");
  const button = el("button", { type: "button", class: "btn btn-block" }, needsRegistration ? "設定 Passkey 並登入" : "用 Passkey 確認登入");
  button.addEventListener("click", async () => {
    button.disabled = true;
    status.replaceChildren();
    try {
      if (needsRegistration) {
        const options = await api<Parameters<typeof startRegistration>[0]["optionsJSON"]>("/api/passkey/register/options", {
          method: "POST",
        });
        const attestation = await startRegistration({ optionsJSON: options });
        await api("/api/passkey/register", { method: "POST", body: { ...attestation, device: navigator.platform || "passkey" } });
      } else {
        const options = await api<Parameters<typeof startAuthentication>[0]["optionsJSON"]>("/api/passkey/login/options", {
          method: "POST",
        });
        const assertion = await startAuthentication({ optionsJSON: options });
        await api("/api/passkey/login", { method: "POST", body: assertion });
      }
      await boot();
    } catch (err) {
      const name = (err as { name?: string })?.name;
      status.replaceChildren(
        notice("err", name === "NotAllowedError" ? "已取消或者逾時，請再試一次。" : errText(err))
      );
      button.disabled = false;
    }
  });

  setView(
    el(
      "section",
      { class: "card narrow" },
      el("h1", {}, needsRegistration ? "設定 Passkey" : "第二步：Passkey"),
      el("p", { class: "muted small" }, `電郵已驗證：${email}`),
      el(
        "p",
        { class: "small" },
        needsRegistration
          ? "第一次登入要登記一個 Passkey。之後每次登入都要用佢，就算電郵被盜，冇你部機都入唔到。"
          : "用你登記咗嘅 Passkey（Face ID／指紋）完成登入。"
      ),
      button,
      status
    )
  );
}

// ------------------------------------------------------------------
// 管理介面
// ------------------------------------------------------------------

type Tab = "requests" | "invite" | "users" | "audit";
let currentTab: Tab = "requests";

interface SignupRequest {
  id: string;
  provider: string;
  email: string | null;
  display_name: string | null;
  referral_source: string | null;
  region: string | null;
  status: string;
  created_at: string;
}
interface User {
  id: string;
  name: string;
  email: string | null;
  whatsapp: string | null;
  auth_provider: string | null;
  status: string;
  created_at: string;
  last_verified_at: string | null;
  usage: { chat: number; tts: number; transcribe: number };
}
interface AuditEntry {
  id: string;
  at: string;
  admin_email: string;
  action: string;
  target_email: string | null;
  detail: Record<string, unknown> | null;
}

const STATUS_TEXT: Record<string, string> = {
  pending: "待批",
  approved: "已批准",
  rejected: "已拒絕",
  invited: "已邀請（未登入）",
  active: "使用中",
  blocked: "已停用",
};
const ACTION_TEXT: Record<string, string> = {
  approve: "批准申請",
  reject: "拒絕申請",
  invite: "邀請",
  block: "停用",
  unblock: "恢復",
  login: "Admin 登入",
  passkey_register: "登記 Passkey",
};
const pill = (status: string) => el("span", { class: `pill pill-${status}` }, STATUS_TEXT[status] || status);

async function renderDashboard(email: string) {
  whoami.hidden = false;
  whoamiEmail.textContent = email;

  const tabs = el("nav", { class: "tabs", role: "tablist" });
  const panel = el("section", { id: "panel" });
  const tabDefs: Array<[Tab, string]> = [
    ["requests", "申請"],
    ["invite", "邀請"],
    ["users", "用戶"],
    ["audit", "審計記錄"],
  ];
  for (const [id, label] of tabDefs) {
    const btn = el("button", { type: "button", class: "tab", role: "tab", id: `tab-${id}`, "aria-selected": String(id === currentTab) }, label);
    btn.addEventListener("click", () => {
      currentTab = id;
      for (const b of tabs.querySelectorAll("button")) b.setAttribute("aria-selected", String(b.id === `tab-${id}`));
      void renderTab(panel);
    });
    tabs.append(btn);
  }
  setView(tabs, panel);
  await renderTab(panel);

  // 待批數字顯示喺申請分頁
  api<{ requests: SignupRequest[] }>("/api/requests?status=pending")
    .then(({ requests }) => {
      if (requests.length > 0) tabs.querySelector("#tab-requests")?.append(el("span", { class: "count" }, String(requests.length)));
    })
    .catch(() => {});
}

async function renderTab(panel: HTMLElement) {
  panel.replaceChildren(el("p", { class: "muted" }, "載入中…"));
  try {
    if (currentTab === "requests") await renderRequests(panel, "pending");
    else if (currentTab === "invite") renderInvite(panel);
    else if (currentTab === "users") await renderUsers(panel);
    else await renderAudit(panel);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return renderLogin(notice("info", ERROR_TEXT.admin_login_required));
    panel.replaceChildren(notice("err", errText(err)));
  }
}

async function renderRequests(panel: HTMLElement, status: string) {
  const { requests } = await api<{ requests: SignupRequest[] }>(`/api/requests?status=${encodeURIComponent(status)}`);
  const filter = el(
    "select",
    { id: "request-filter", "aria-label": "篩選申請狀態" },
    ...["pending", "approved", "rejected", "all"].map((s) =>
      el("option", { value: s, selected: s === status }, s === "all" ? "全部" : STATUS_TEXT[s])
    )
  );
  filter.addEventListener("change", () => void renderRequests(panel, filter.value).catch((e) => panel.replaceChildren(notice("err", errText(e)))));
  const status_ = el("div");

  const rows = requests.map((r) => {
    const actions = el("td", { class: "actions" });
    if (r.status === "pending") {
      const approve = el("button", { type: "button", class: "btn btn-sm" }, "批准");
      const reject = el("button", { type: "button", class: "btn btn-sm btn-ghost" }, "拒絕");
      approve.addEventListener("click", async () => {
        approve.disabled = reject.disabled = true;
        try {
          const res = await api<{ emailed: boolean }>(`/api/requests/${r.id}/approve`, { method: "POST", body: {} });
          status_.replaceChildren(
            notice("ok", `已批准 ${r.email ?? r.display_name ?? ""}${res.emailed ? "，已寄出通知電郵。" : "（通知電郵寄唔到，請自行通知）。"}`)
          );
          await renderRequests(panel, status);
          panel.prepend(status_);
        } catch (err) {
          status_.replaceChildren(notice("err", errText(err)));
          approve.disabled = reject.disabled = false;
        }
      });
      reject.addEventListener("click", async () => {
        if (!reject.dataset.confirm) {
          reject.dataset.confirm = "1";
          reject.textContent = "確定拒絕？";
          reject.className = "btn btn-sm btn-danger";
          return;
        }
        approve.disabled = reject.disabled = true;
        try {
          await api(`/api/requests/${r.id}/reject`, { method: "POST", body: {} });
          await renderRequests(panel, status);
        } catch (err) {
          status_.replaceChildren(notice("err", errText(err)));
          approve.disabled = reject.disabled = false;
        }
      });
      actions.append(approve, reject);
    }
    return el(
      "tr",
      {},
      el("td", {}, r.display_name || "—"),
      el("td", { class: "mono" }, r.email || `(${r.provider})`),
      el("td", {}, r.region || "—"),
      el("td", {}, r.referral_source || "—"),
      el("td", { class: "num" }, fmtTime(r.created_at)),
      el("td", {}, pill(r.status)),
      actions
    );
  });

  panel.replaceChildren(
    el("div", { class: "toolbar" }, el("h2", {}, "申請"), filter),
    status_,
    requests.length === 0
      ? el("div", { class: "card empty" }, status === "pending" ? "冇待批申請。" : "冇記錄。")
      : el(
          "div",
          { class: "table-wrap" },
          el(
            "table",
            {},
            el("thead", {}, el("tr", {}, ...["稱呼", "電郵", "地區", "點知道", "申請時間", "狀態", ""].map((h) => el("th", {}, h)))),
            el("tbody", {}, ...rows)
          )
        )
  );
}

function renderInvite(panel: HTMLElement) {
  const email = el("input", { type: "email", id: "invite-email", required: true, placeholder: "friend@example.com" });
  const name = el("input", { type: "text", id: "invite-name", maxlength: "60", placeholder: "（可選）稱呼" });
  const submit = el("button", { type: "submit", class: "btn" }, "邀請");
  const status = el("div");
  const form = el(
    "form",
    { class: "form-row" },
    el("div", {}, el("label", { for: "invite-email" }, "電郵"), email),
    el("div", {}, el("label", { for: "invite-name" }, "稱呼"), name),
    submit
  );
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    try {
      const res = await api<{ emailed: boolean }>("/api/invite", { method: "POST", body: { email: email.value, name: name.value } });
      status.replaceChildren(
        notice("ok", `已邀請 ${email.value}${res.emailed ? "，邀請電郵已寄出。" : "（邀請電郵寄唔到，請自行通知）。"}佢用呢個電郵登入就可以直接用。`)
      );
      form.reset();
    } catch (err) {
      status.replaceChildren(notice("err", errText(err)));
    } finally {
      submit.disabled = false;
    }
  });
  panel.replaceChildren(
    el(
      "div",
      { class: "card" },
      el("h2", {}, "邀請用戶"),
      el("p", { class: "muted small" }, "被邀請嘅人唔使申請同審批，用呢個電郵登入就可以直接開始。"),
      form,
      status
    )
  );
}

async function renderUsers(panel: HTMLElement) {
  const { users } = await api<{ users: User[] }>("/api/users");
  const status = el("div");
  const rows = users.map((u) => {
    const actions = el("td", { class: "actions" });
    const blocking = u.status !== "blocked";
    const toggle = el("button", { type: "button", class: `btn btn-sm ${blocking ? "btn-ghost" : ""}` }, blocking ? "停用" : "恢復");
    toggle.addEventListener("click", async () => {
      if (blocking && !toggle.dataset.confirm) {
        toggle.dataset.confirm = "1";
        toggle.textContent = "確定停用？";
        toggle.className = "btn btn-sm btn-danger";
        return;
      }
      toggle.disabled = true;
      try {
        await api(`/api/users/${u.id}/status`, { method: "POST", body: { status: blocking ? "blocked" : "active" } });
        await renderUsers(panel);
        panel.prepend(notice("ok", blocking ? `已停用 ${u.email ?? u.name}（60 秒內生效）。` : `已恢復 ${u.email ?? u.name}。`));
      } catch (err) {
        status.replaceChildren(notice("err", errText(err)));
        toggle.disabled = false;
      }
    });
    actions.append(toggle);
    return el(
      "tr",
      {},
      el("td", {}, u.name),
      el("td", { class: "mono" }, u.email || u.whatsapp || "—"),
      el("td", {}, el("span", { class: "pill pill-neutral" }, u.auth_provider || (u.whatsapp ? "whatsapp" : "—"))),
      el("td", {}, pill(u.status)),
      el("td", { class: "num" }, `${u.usage.chat} / ${u.usage.tts} / ${u.usage.transcribe}`),
      el("td", { class: "num" }, fmtTime(u.last_verified_at)),
      el("td", { class: "num" }, fmtTime(u.created_at)),
      actions
    );
  });
  panel.replaceChildren(
    el("div", { class: "toolbar" }, el("h2", {}, `用戶（${users.length}）`)),
    status,
    el(
      "div",
      { class: "table-wrap" },
      el(
        "table",
        {},
        el(
          "thead",
          {},
          el("tr", {}, ...["稱呼", "電郵／電話", "登入方式", "狀態", "今日 對話/語音/轉錄", "最後登入", "加入時間", ""].map((h) => el("th", {}, h)))
        ),
        el("tbody", {}, ...rows)
      )
    )
  );
}

async function renderAudit(panel: HTMLElement) {
  const { entries } = await api<{ entries: AuditEntry[] }>("/api/audit?limit=200");
  const rows = entries.map((e) =>
    el(
      "tr",
      {},
      el("td", { class: "num" }, fmtTime(e.at)),
      el("td", { class: "mono" }, e.admin_email),
      el("td", {}, ACTION_TEXT[e.action] || e.action),
      el("td", { class: "mono" }, e.target_email || "—"),
      el("td", { class: "mono small" }, e.detail ? JSON.stringify(e.detail) : "")
    )
  );
  panel.replaceChildren(
    el("div", { class: "toolbar" }, el("h2", {}, "審計記錄（最近 200 條）")),
    entries.length === 0
      ? el("div", { class: "card empty" }, "未有記錄。")
      : el(
          "div",
          { class: "table-wrap" },
          el(
            "table",
            {},
            el("thead", {}, el("tr", {}, ...["時間", "Admin", "動作", "對象", "詳情"].map((h) => el("th", {}, h)))),
            el("tbody", {}, ...rows)
          )
        )
  );
}

// ------------------------------------------------------------------
// 啟動
// ------------------------------------------------------------------

logoutBtn.addEventListener("click", async () => {
  await api("/api/logout", { method: "POST" }).catch(() => {});
  renderLogin(notice("info", "已登出。"));
});

async function boot() {
  const token = /(?:^#|&)token=([A-Za-z0-9_-]+)/.exec(location.hash)?.[1];
  if (token) return handleLinkFromEmail(token);

  const me = await api<{ email: string | null; stage: "email" | "full" | null }>("/api/me").catch(() => ({
    email: null,
    stage: null,
  }));
  if (me.email && me.stage === "full") return renderDashboard(me.email);
  if (me.email && me.stage === "email") {
    // 電郵已驗證但未完成 passkey（例如重新整理咗頁面）：問 server 要唔要登記
    return renderPasskeyStep(me.email, false);
  }
  renderLogin();
}

void boot();
